import {
  Accordion,
  accordionItemBuilder,
  Text,
  TextButton,
} from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import _ from 'lodash';
import { FC, useMemo } from 'react';
import AccordionServiceItem from './AccordionServiceItem';
import { Category } from '@common/types';

type ServiceAccordionProps = {
  offeredServices: Category[];
  firstAccordionRowIsOpen: boolean;
  onToggleFirstAccordion: () => void;
};

const ServiceAccordion: FC<ServiceAccordionProps> = ({
  offeredServices,
  firstAccordionRowIsOpen,
  onToggleFirstAccordion,
}) => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return _.map(offeredServices, (category, categoryIndex) => {
      const isFirstItem = categoryIndex === 0;
      return accordionItemBuilder({
        initiallyOpen: isFirstItem,
        ...(isFirstItem && {
          open: firstAccordionRowIsOpen,
          onToggle: onToggleFirstAccordion,
        }),
        title: (
          <Text size="medium" dataHook={`accordion-title-${categoryIndex}`}>
            {`${t(`explore.services.${category.displayKey}`)} (${_.size(
              category.services,
            )})`}
          </Text>
        ),
        buttonType: 'node',
        showLabel: 'always',
        expandLabel: (
          <TextButton>
            <ChevronDown color="black" />
          </TextButton>
        ),
        collapseLabel: (
          <TextButton>
            <ChevronUp color="black" />
          </TextButton>
        ),
        children: (
          <>
            {_.map(category?.services, (service, serviceIndex) => {
              const isLastCategory =
                categoryIndex === _.size(offeredServices) - 1;
              const isLastService =
                serviceIndex === _.size(category?.services) - 1;

              return (
                <div key={service.id || serviceIndex}>
                  <AccordionServiceItem
                    service={service}
                    isLastServiceAndCategory={isLastCategory && isLastService}
                    useSmallerBottomPadding={isLastService}
                  />
                </div>
              );
            })}
          </>
        ),
      });
    });
  }, [offeredServices, firstAccordionRowIsOpen, onToggleFirstAccordion, t]);

  return (
    <Accordion
      dataHook="starting-rates-accordion"
      size="tiny"
      skin="light"
      hideShadow
      horizontalPadding="none"
      multiple
      items={items || []}
    />
  );
};

export default ServiceAccordion;
